import Space from 'antd/lib/space';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { KickoffActionStatus, ReleaseStepKickoff, ReleaseType } from '../types';
import { runwayColors } from './styles/globalStyles';
import { BranchesOutlined, InfoCircleOutlined, PullRequestOutlined, FileAddOutlined } from '@ant-design/icons';
import { ReleaseStepComponentProps } from '../typescriptHelpers';
import Tooltip from 'antd/lib/tooltip';
import { ReleaseUpdatable } from '../actions/updatable';
import { BaseUpdateReleaseRequest, BumpVersionRequest, tryUpdateRelease } from '../actions/release';
import Popconfirm from 'antd/lib/popconfirm';
import PermissionedActionButton from './HOCs/PermissionedActionButton';
import { RunwayUserAction } from '../helpers/aclHelpers';
import { selectRelease } from '../selectors/selectors';
import { RootState } from '../reducers/reducers';
import Modal from 'antd/lib/modal';
import { globalMessage } from '../utils/globalMessage';
import { useError, useIsLoading, useReleaseStep, useUpdated } from '@/utils/hooks/helpers';

interface KickoffStepFooterProps extends ReleaseStepComponentProps {
    shouldShowEmptyState?: boolean;
    workingBranch: string;
}

const KickoffStepFooter: React.FC<KickoffStepFooterProps> = (props) => {
    const { releaseId, shouldShowEmptyState } = props;
    const dispatch = useDispatch();
    const [showCreateReleaseBranchPopConfirm, setShowReleaseBranchPopConfirm] = React.useState(false);
    const [showPromoteCodePopConfirm, setShowPromoteCodePopConfirm] = React.useState(false);
    const [showBumpVersionPopConfirm, setShowBumpVersionPopConfirm] = React.useState(false);
    const [bumpVersionOnDefaultBranch, setBumpVersionOnDefaultBranch] = React.useState(true);

    const createReleaseBranchIsLoading = useIsLoading(ReleaseUpdatable.CreateReleaseBranch, props.releaseId);
    const createReleaseBranchSuccess = useUpdated(ReleaseUpdatable.CreateReleaseBranch, props.releaseId);
    const createReleaseBranchFailed = useError(ReleaseUpdatable.CreateReleaseBranch, props.releaseId);

    const promoteBranchIsLoading = useIsLoading(ReleaseUpdatable.PromoteWorkingBranch, props.releaseId);
    const promoteBranchSuccess = useUpdated(ReleaseUpdatable.PromoteWorkingBranch, props.releaseId);
    const promoteBranchError = useError(ReleaseUpdatable.PromoteWorkingBranch, props.releaseId);

    const bumpVersionIsLoading = useIsLoading(ReleaseUpdatable.BumpVersion, props.releaseId);
    const bumpVersionError = useError(ReleaseUpdatable.BumpVersion, props.releaseId);
    const bumpVersionUpdated = useUpdated(ReleaseUpdatable.BumpVersion, props.releaseId);

    const isHotfix = useSelector((state: RootState) => {
        return selectRelease(state, props.releaseId).type === ReleaseType.Hotfix;
    });
    const workingBranch = props.workingBranch;
    const releaseStep = useReleaseStep(props) as ReleaseStepKickoff;

    const createReleaseBranch = () => {
        const request: BaseUpdateReleaseRequest = {
            orgId: props.orgId,
            appId: props.appId,
            releaseId,
        };

        dispatch(tryUpdateRelease(ReleaseUpdatable.CreateReleaseBranch, request, releaseId));
    };

    const bumpVersion = (onBranch: string) => {
        const bumpOnReleaseBranch = onBranch === releaseBranch;

        const request: BumpVersionRequest = {
            orgId: props.orgId,
            appId: props.appId,
            releaseId,
            bumpOnReleaseBranch,
        };

        dispatch(tryUpdateRelease(ReleaseUpdatable.BumpVersion, request, releaseId));
    };

    const branchExists = releaseStep.data.doesReleaseBranchExist;
    const releaseBranch = releaseStep.data.releaseBranch;

    const hotfixBase = releaseStep.data.hotfixBranchingBase;
    const base = isHotfix ? hotfixBase : workingBranch;
    const previousReleaseVersion = releaseStep.data.previousReleaseVersion;
    const isStaticReleaseBranch = releaseStep.data.isStaticReleaseBranch;
    const canPerformCodePromotion = releaseStep.data.canPerformCodePromotion;
    const codePromotionStatus = releaseStep.data.codePromotionStatus;
    const isReleaseBranchSameAsWorkingBranch = releaseBranch === workingBranch;

    const releaseBranchVersionData = releaseStep.data.releaseBranchCodeVersionData;
    const workingBranchVersionData = releaseStep.data.workingBranchCodeVersionData;

    const defaultVersionData =
        isHotfix || isReleaseBranchSameAsWorkingBranch ? releaseBranchVersionData : workingBranchVersionData; // hotfix or pure-trunk teams should default to using release branch data
    const alternateVersionData =
        isHotfix || isReleaseBranchSameAsWorkingBranch ? workingBranchVersionData : releaseBranchVersionData;

    const isHotfixUnableToBranch = isHotfix && !base;

    const showBumpVersionConfirm = (useDefaultBranch: boolean) => {
        const branchData = useDefaultBranch ? defaultVersionData : alternateVersionData;

        Modal.confirm({
            title: `Confirm bump version`,
            content: `Are you sure you want to bump the version on the '${branchData.branchName}' branch?`,
            icon: null,
            onOk() {
                bumpVersion(branchData.branchName);
            },
            okText: `Yes, bump version to ${branchData.targetCodeVersion}`,
            onCancel() {
                setBumpVersionOnDefaultBranch(true);
            },
        });
    };

    const promoteCodeFromWorkingBranch = () => {
        const request: BaseUpdateReleaseRequest = {
            orgId: props.orgId,
            appId: props.appId,
            releaseId,
        };

        dispatch(tryUpdateRelease(ReleaseUpdatable.PromoteWorkingBranch, request, releaseId));
    };

    React.useEffect(() => {
        if (createReleaseBranchSuccess) {
            globalMessage.success(`Release branch ${releaseBranch} created!`);

            setShowReleaseBranchPopConfirm(false);
        }
    }, [createReleaseBranchSuccess]);

    React.useEffect(() => {
        if (createReleaseBranchFailed) {
            globalMessage.error(createReleaseBranchFailed);
        }
    }, [createReleaseBranchFailed]);

    React.useEffect(() => {
        if (promoteBranchSuccess) {
            switch (codePromotionStatus) {
                case KickoffActionStatus.Detected:
                    globalMessage.success(`Working branch ${workingBranch} has been promoted to ${releaseBranch}`);
                    break;
                case KickoffActionStatus.Incomplete:
                    globalMessage.info(
                        `A pull request has been opened to merge working branch ${workingBranch} to ${releaseBranch}. Please review to proceed.`,
                    );
                    break;
                case KickoffActionStatus.NotDetected:
                    globalMessage.error(
                        `Could not successfully promote working branch ${workingBranch} to ${releaseBranch}`,
                    );
                    break;
            }

            setShowPromoteCodePopConfirm(false);
        }
    }, [promoteBranchSuccess]);

    React.useEffect(() => {
        if (promoteBranchError) {
            globalMessage.error(promoteBranchError);
        }
    }, [promoteBranchError]);

    React.useEffect(() => {
        if (bumpVersionError) {
            globalMessage.error(bumpVersionError);
        }
    }, [bumpVersionError]);

    React.useEffect(() => {
        if (bumpVersionUpdated) {
            const branchData = bumpVersionOnDefaultBranch ? defaultVersionData : alternateVersionData;
            switch (branchData.bumpVersionStatus) {
                case KickoffActionStatus.Detected:
                    globalMessage.success(
                        `The version in the ${branchData.branchName} branch has been bumped to ${branchData.targetCodeVersion}`,
                    );
                    break;
                case KickoffActionStatus.Incomplete:
                    globalMessage.info(
                        `A pull request has been opened to bump the ${branchData.branchName} branch version to ${branchData.targetCodeVersion}. Please review to proceed.`,
                    );
                    break;
                case KickoffActionStatus.NotDetected:
                    globalMessage.error(
                        `Could not successfully bump the ${branchData.branchName} branch version to ${branchData.targetCodeVersion}`,
                    );
                    break;
            }

            setShowBumpVersionPopConfirm(false);
        }
    }, [bumpVersionUpdated]);

    const renderBumpVersionCTA = () => {
        if (isReleaseBranchSameAsWorkingBranch || shouldShowEmptyState) {
            return (
                <Popconfirm
                    title={`Are you sure you want to bump the version on the ${defaultVersionData?.branchName} branch?`}
                    open={showBumpVersionPopConfirm}
                    onConfirm={() => bumpVersion(defaultVersionData?.branchName)}
                    okButtonProps={{ loading: bumpVersionIsLoading }}
                    okText="Yes, bump version"
                    onCancel={() => setShowBumpVersionPopConfirm(false)}
                >
                    {!defaultVersionData?.branchName && !shouldShowEmptyState ? null : (
                        <PermissionedActionButton
                            actionType={RunwayUserAction.UABumpVersion}
                            releaseId={props.releaseId}
                            icon={<FileAddOutlined style={{ color: runwayColors.statusGreen }} />}
                            onClick={() => setShowBumpVersionPopConfirm(true)}
                            loading={bumpVersionIsLoading}
                            disabled={!defaultVersionData?.canPerformBumpVersion}
                        >
                            {shouldShowEmptyState ?
                                <>Bump version on main to X.X.X</>
                            :   <>
                                    Bump version on &#39;{defaultVersionData?.branchName}&#39; to{' '}
                                    {defaultVersionData?.targetCodeVersion}
                                </>
                            }
                        </PermissionedActionButton>
                    )}
                </Popconfirm>
            );
        }

        const onClickBumpAlternate = () => {
            setBumpVersionOnDefaultBranch(false);
            showBumpVersionConfirm(false);
        };

        return !defaultVersionData?.branchName && !shouldShowEmptyState ?
                null
            :   <PermissionedActionButton
                    actionType={RunwayUserAction.UABumpVersion}
                    menu={{
                        items: [
                            {
                                key: 'bump-alternate',
                                label: `Bump version on '${alternateVersionData?.branchName}' to ${alternateVersionData?.targetCodeVersion}`,
                                icon: <FileAddOutlined style={{ color: runwayColors.statusGreen }} />,
                                disabled: !alternateVersionData?.canPerformBumpVersion,
                                onClick: onClickBumpAlternate,
                            },
                        ],
                    }}
                    disabled={shouldShowEmptyState}
                    buttonsRender={([leftButton, rightButton]) =>
                        shouldShowEmptyState ?
                            [
                                React.cloneElement(leftButton as React.ReactElement<any, string>, {
                                    disabled: true,
                                }),

                                React.cloneElement(rightButton as React.ReactElement<any, string>, {
                                    disabled: true,
                                }),
                            ]
                        :   [
                                React.cloneElement(leftButton as React.ReactElement<any, string>, {
                                    disabled: !defaultVersionData?.canPerformBumpVersion,
                                    loading: bumpVersionOnDefaultBranch && bumpVersionIsLoading,
                                }),

                                React.cloneElement(rightButton as React.ReactElement<any, string>, {
                                    disabled: !alternateVersionData?.canPerformBumpVersion,
                                    loading: !bumpVersionOnDefaultBranch && bumpVersionIsLoading,
                                }),
                            ]
                    }
                    trigger={['click']}
                    loading={bumpVersionIsLoading}
                    onClick={() => {
                        setBumpVersionOnDefaultBranch(true);
                        showBumpVersionConfirm(bumpVersionOnDefaultBranch);
                    }}
                >
                    {shouldShowEmptyState ?
                        <>
                            <>Bump version on main to X.X.X</>
                        </>
                    :   <>
                            Bump version on &#39;{defaultVersionData?.branchName}&#39; to{' '}
                            {defaultVersionData?.targetCodeVersion}
                        </>
                    }
                </PermissionedActionButton>;
    };

    return (
        <Space size="large" align="center">
            {renderBumpVersionCTA()}
            {!isStaticReleaseBranch && (
                <Space align="center">
                    <Popconfirm
                        title="Are you sure you want to create the release branch?"
                        open={showCreateReleaseBranchPopConfirm}
                        onConfirm={createReleaseBranch}
                        okButtonProps={{ loading: createReleaseBranchIsLoading }}
                        okText="Yes, create release branch"
                        onCancel={() => setShowReleaseBranchPopConfirm(false)}
                    >
                        <PermissionedActionButton
                            actionType={RunwayUserAction.UACreateBranchPromoteCode}
                            releaseId={props.releaseId}
                            icon={<BranchesOutlined style={{ color: runwayColors.statusGreen }} />}
                            onClick={() => setShowReleaseBranchPopConfirm(true)}
                            loading={createReleaseBranchIsLoading}
                            disabled={branchExists || isHotfixUnableToBranch || releaseBranch == ''}
                        >
                            {shouldShowEmptyState ?
                                <>Create release branch</>
                            :   <>
                                    Create {releaseBranch != '' ? `'` + releaseBranch + `'` : 'unknown'} branch{' '}
                                    {base && `from '${base}'`}
                                </>
                            }
                        </PermissionedActionButton>
                    </Popconfirm>
                    {!branchExists && (
                        <Tooltip
                            title={
                                isHotfixUnableToBranch ?
                                    `You must tag your previous release ${
                                        previousReleaseVersion ? '(' + previousReleaseVersion + ')' : ''
                                    } in order to create this hotfix release branch.`
                                :   'Kick off this release by creating a release branch now.'
                            }
                        >
                            <InfoCircleOutlined style={{ color: runwayColors.info }} />
                        </Tooltip>
                    )}
                </Space>
            )}
            {isStaticReleaseBranch && (
                <Space align="center">
                    <Popconfirm
                        title="Are you sure you want to promote code from your working branch to your release branch?"
                        open={showPromoteCodePopConfirm}
                        onConfirm={promoteCodeFromWorkingBranch}
                        okButtonProps={{ loading: promoteBranchIsLoading }}
                        onCancel={() => setShowPromoteCodePopConfirm(false)}
                        okText="Yes, promote code from working branch"
                    >
                        <PermissionedActionButton
                            actionType={RunwayUserAction.UACreateBranchPromoteCode}
                            releaseId={props.releaseId}
                            icon={<PullRequestOutlined style={{ color: runwayColors.statusGreen }} />}
                            onClick={() => setShowPromoteCodePopConfirm(true)}
                            loading={promoteBranchIsLoading}
                            disabled={
                                !canPerformCodePromotion || codePromotionStatus === KickoffActionStatus.Incomplete
                            }
                        >
                            Promote code from {workingBranch ? "'" + workingBranch + "'" : 'working branch'} →{' '}
                            {releaseBranch ? "'" + releaseBranch + "'" : 'release branch'}
                        </PermissionedActionButton>
                    </Popconfirm>
                    <Tooltip title="Promote the latest code on your working branch to your release branch">
                        <InfoCircleOutlined style={{ color: runwayColors.info }} />
                    </Tooltip>
                </Space>
            )}
        </Space>
    );
};

export default KickoffStepFooter;
